import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const TirupatiDarshanam = () => {
  // ── Features data ──
  const features = [
    {
      icon: "🎫",
      title: "Special Entry Darshan",
      desc: "Skip the long queues with our special entry tickets. Enjoy a seamless and divine darshan of Lord Venkateswara with minimal wait time.",
    },
    {
      icon: "🏨",
      title: "Comfortable Accommodation",
      desc: "Stay in clean, well-maintained rooms at our dedicated guest house near the temple. Accommodation is provided for the donor.",
    },
    {
      icon: "🍛",
      title: "Free Meals & Prasadam",
      desc: "Savour authentic South Indian meals and temple prasadam throughout your stay. Food is hygienically prepared and served with utmost devotion.",
    },
    {
      icon: "🚐",
      title: "Transport Arrangements",
      desc: "We provide pick-up and drop-off from the railway station or bus stand. Local transport to the temple and other nearby attractions is also arranged.",
    },
    {
      icon: "👨‍👩‍👧‍👦",
      title: "Family Inclusive",
      desc: "The package is valid for the donor family members. A memorable pilgrimage experience for your entire family.",
    },
    {
      icon: "📅",
      title: "Flexible Booking",
      desc: "Choose your preferred dates (subject to availability). Our team will coordinate with the temple authorities to ensure a smooth experience.",
    },
  ];

  // ── How to Avail ──
  const steps = [
    {
      icon: "1️⃣",
      title: "Donate to the Trust",
      desc: "Make a donation of ₹25,000 or more (or as per current promotion) to Venkatadri Seva Trust. Every rupee goes towards our charitable activities.",
    },
    {
      icon: "2️⃣",
      title: "Register Your Interest",
      desc: "Fill out the registration form with your preferred dates and family details. Our team will confirm availability and send you a confirmation.",
    },
    {
      icon: "3️⃣",
      title: "Receive Your Package",
      desc: "We will send you the darshan tickets, accommodation details, and transport instructions. Our local coordinator will be available to assist you.",
    },
    {
      icon: "4️⃣",
      title: "Enjoy Your Pilgrimage",
      desc: "Experience the divine blessings of Lord Venkateswara with a hassle-free pilgrimage, fully arranged by us as a gesture of gratitude.",
    },
  ];

  // ── Testimonials ──
  const testimonials = [
    {
      text: "Venkatadri Seva Trust made our Tirupati trip unforgettable. The special darshan ticket saved us hours of waiting. The accommodation was comfortable and the food was homely. We are truly grateful.",
      author: "Srinivas & Family",
      city: "Hyderabad",
    },
    {
      text: "We have been donating to the Trust for years. When we learned about this pilgrimage package, we were thrilled. Everything was perfectly organized—transport, stay, and darshan. A blessed experience.",
      author: "Lakshmi Narayanan",
      city: "Chennai",
    },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="tirupati-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-hotel mr-2"></i> Donor Benefit
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Tirupathi Darshanam &amp; Accommodation
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              A free pilgrimage experience for our generous donors – special darshan, food, stay, and transport
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                A Pilgrimage As a Token of Gratitude
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              At Venkatadri Seva Trust, we believe in honouring our donors who make our charitable work possible.
              As a gesture of deep appreciation, we are pleased to offer a <strong>free pilgrimage package to
                Tirupathi</strong> for our valued donors.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              This package includes <strong>special entry darshan tickets</strong> (no waiting in queues),
              <strong>comfortable accommodation</strong> near the temple, <strong>free meals and prasadam</strong>,
              and <strong>local transport</strong>. Our aim is to ensure you have a blessed and hassle-free
              experience, so you can focus on the divine.
            </p>
          </div>
        </div>
      </section>

      {/* ========== WHAT'S INCLUDED ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">What's Included</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Everything you need for a comfortable and divine pilgrimage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{feature.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW TO AVAIL ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">How to Avail This Offer</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Four simple steps to your free pilgrimage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center"
              >
                <div className="text-5xl mb-3">{step.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800">{step.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-yellow-100 text-lg">
              <i className="fas fa-info-circle mr-2"></i>
              Minimum donation amount: <strong className="text-white">₹25,000</strong> (subject to change).
              Please contact us for current eligibility.
            </p>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Donor Experiences</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-white border border-white/20">
                <div className="text-4xl text-yellow-300 mb-2">“</div>
                <p className="text-white/90 italic">{testimonial.text}</p>
                <div className="mt-4">
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-yellow-100/70 text-sm">{testimonial.city}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CALL TO ACTION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Book Your Pilgrimage Today</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 mb-8">
              Donate to the Trust and experience the divine grace of Lord Venkateswara with our free pilgrimage
              package. Let us take care of every detail while you focus on your devotion.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-phone-alt mr-2"></i> Enquire Now
              </a>
            </div>
            <p className="text-yellow-100/70 text-sm mt-6">
              * Terms and conditions apply. Donation amount eligibility may vary. Please contact us for more details.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default TirupatiDarshanam;